import { buildAppsationsFunctionUrl } from '@/lib/appsationsSdkEndpoints';

export type GitHubProxyMode = 'list' | 'stats' | 'ping' | 'whoami' | 'repo' | 'commit' | 'tree' | 'blob';

const trimTrailingSlashes = (value: string): string => value.replace(/\/+$/, '');

const normalizeExplicitProxyBase = (value: string): string => {
  const explicit = trimTrailingSlashes(value.trim());
  if (!explicit) return '';
  if (/\/api\/github$/i.test(explicit) || /\/githubProxy$/i.test(explicit)) return explicit;
  return `${explicit}/api/github`;
};

export const resolveAppsationsGitHubProxyBaseUrl = (): string => {
  const explicit = normalizeExplicitProxyBase(import.meta.env.VITE_GITHUB_PROXY_ORIGIN ?? '');
  if (explicit) return explicit;

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host.endsWith('.web.app') || host.endsWith('.firebaseapp.com')) return '/api/github';
  }

  const projectId = (import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '').trim();
  if (projectId) return `https://${projectId}.web.app/api/github`;

  return buildAppsationsFunctionUrl('githubProxy');
};

export const buildAppsationsGitHubProxyUrl = (payload: {
  mode: GitHubProxyMode;
  owner: string;
  repo: string;
  page?: number;
  perPage?: number;
  sha?: string;
}): string => {
  const baseUrl = resolveAppsationsGitHubProxyBaseUrl();
  const params = new URLSearchParams();
  params.set('mode', payload.mode);
  params.set('owner', payload.owner);
  params.set('repo', payload.repo);
  if (typeof payload.page === 'number') params.set('page', String(payload.page));
  if (typeof payload.perPage === 'number') params.set('per_page', String(payload.perPage));
  if (payload.sha) params.set('sha', payload.sha);
  return `${baseUrl}?${params.toString()}`;
};

export const parseAppsationsGitHubError = async (response: Response): Promise<string | null> => {
  try {
    const cloned = response.clone();
    try {
      const data = await cloned.json();
      if (data?.message) return String(data.message);
      if (data?.error) return String(data.error);
    } catch {
      // ignore
    }

    const text = await response.text();
    if (text && text.trim().length > 0) {
      return text.trim().slice(0, 400);
    }
  } catch {
    return null;
  }
  return null;
};

export const parseAppsationsGitHubJson = async <T,>(response: Response, context: string): Promise<T> => {
  const text = await response.text();
  const trimmed = text.trim();
  const contentType = response.headers.get('content-type') ?? '';
  const responseUrl = response.url ? ` Response URL: ${response.url}.` : '';

  if (!trimmed) {
    throw new Error(`${context} returned an empty response.`);
  }

  try {
    return JSON.parse(trimmed) as T;
  } catch {
    const looksLikeHtml =
      contentType.includes('text/html') ||
      trimmed.startsWith('<!doctype html') ||
      trimmed.startsWith('<html') ||
      trimmed.startsWith('<!DOCTYPE html');
    if (looksLikeHtml) {
      throw new Error(
        `${context} returned HTML instead of JSON.${responseUrl} This usually means the /api/github proxy isn’t being served by your backend (Firebase Hosting rewrite) or you’re on a preview host that can’t run the proxy. If you’re not on a *.web.app / *.firebaseapp.com host, set VITE_GITHUB_PROXY_ORIGIN to your Firebase Hosting origin (for example: https://<project-id>.web.app) so the Developer page can reach the deployed proxy.`
      );
    }
    if (contentType && !contentType.includes('json')) {
      throw new Error(
        `${context} returned an unexpected content-type (${contentType}).${responseUrl} Verify the /api/github proxy returns JSON.`
      );
    }
    const preview = trimmed.length > 200 ? `${trimmed.slice(0, 200)}…` : trimmed;
    throw new Error(`${context} returned invalid JSON. ${preview}`);
  }
};

export const describeAppsationsGitHubError = async (response: Response): Promise<string> => {
  const baseMessage = await parseAppsationsGitHubError(response);
  const remaining = response.headers.get('x-ratelimit-remaining');
  const reset = response.headers.get('x-ratelimit-reset');
  const resetAt = reset && Number.isFinite(Number(reset)) ? new Date(Number(reset) * 1000).toLocaleString() : null;
  const requestId = response.headers.get('x-github-request-id');
  const ssoHeader = response.headers.get('x-github-sso');
  let hint: string | null = null;

  if (response.status === 401) {
    hint = 'Ensure the server Actions secret (ACTIONS_TOKEN) is configured (Firebase Function proxy).';
  } else if (response.status === 403) {
    if (remaining === '0' && resetAt) {
      hint = `GitHub rate limit exceeded. Try again after ${resetAt}.`;
    } else {
      hint = 'Access forbidden. Ensure the server Actions secret (ACTIONS_TOKEN) has access to this repo (e.g., “Contents: Read-only”) and meets any org approval/SSO requirements.';
      if (typeof baseMessage === 'string' && baseMessage.toLowerCase().includes('resource not accessible by personal access token')) {
        hint = 'GitHub auth is blocked. Common causes: missing permissions (e.g., “Contents: Read-only”), token not approved by the org, or org restrictions/SSO requirements.';
      }
    }
  } else if (response.status === 404) {
    hint = 'Repo or endpoint not found through the proxy. Verify owner/repo and token access.';
  }

  const parts = [`GitHub request failed (${response.status} ${response.statusText}).`];
  if (baseMessage) parts.push(baseMessage);
  if (hint) parts.push(hint);
  if (requestId) parts.push(`GitHub request id: ${requestId}.`);
  if (ssoHeader) parts.push(`SSO header: ${ssoHeader}.`);
  return parts.join(' ');
};